import { useServerFn } from "@tanstack/react-start";
import { useState, type FormEvent } from "react";
import { ArrowRight } from "lucide-react";

import { sendContactInquiry } from "@/lib/contact.functions";
import { CUSTOM_INTEREST, DEFAULT_INTEREST, INTEREST_OPTIONS } from "@/lib/content";
import { EMAIL, PHONE_DISPLAY } from "@/lib/site";

function Field({
  label,
  name,
  type = "text",
  required = false,
  autoComplete,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  const id = `field-${name}`;
  return (
    <label className="grid gap-2" htmlFor={id}>
      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
        {required && (
          <span className="text-primary" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </span>
      <input
        id={id}
        type={type}
        name={name}
        required={required}
        autoComplete={autoComplete}
        aria-required={required || undefined}
        className="min-h-11 rounded-xl border border-input bg-background px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-ring/25"
      />
    </label>
  );
}

export function ContactForm({
  defaultInterest,
  defaultMessage,
}: {
  defaultInterest?: string;
  defaultMessage?: string;
}) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitInquiry = useServerFn(sendContactInquiry);

  const interestValue =
    defaultInterest === "custom" ? CUSTOM_INTEREST : (defaultInterest ?? DEFAULT_INTEREST);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    setSending(true);
    setError(null);
    try {
      await submitInquiry({
        data: {
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          interest: String(fd.get("interest") ?? ""),
          message: String(fd.get("message") ?? ""),
        },
      });
      setSent(true);
    } catch {
      setError(`Sorry — something went wrong. Please call ${PHONE_DISPLAY} or email ${EMAIL}.`);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="py-14 text-center">
        <h3 className="font-display text-2xl">Thank you!</h3>
        <p className="mt-3 text-sm text-muted-foreground">
          Your note is on its way. Ella will follow up personally — or call {PHONE_DISPLAY} any
          time.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-5" noValidate={false}>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name" name="name" autoComplete="name" required />
        <Field label="Phone" name="phone" type="tel" autoComplete="tel" />
      </div>
      <Field label="Email" name="email" type="email" autoComplete="email" required />

      <label className="grid gap-2" htmlFor="field-interest">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          What are you interested in?
        </span>
        <select
          id="field-interest"
          name="interest"
          defaultValue={
            INTEREST_OPTIONS.includes(interestValue as (typeof INTEREST_OPTIONS)[number])
              ? interestValue
              : DEFAULT_INTEREST
          }
          className="min-h-11 rounded-xl border border-input bg-background px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-ring/25"
        >
          {INTEREST_OPTIONS.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      </label>

      <label className="grid gap-2" htmlFor="field-message">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Message
        </span>
        <textarea
          id="field-message"
          name="message"
          rows={4}
          defaultValue={defaultMessage}
          className="resize-none rounded-xl border border-input bg-background px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-ring/25"
          placeholder="Tell Ella what you're looking for…"
        />
      </label>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={sending}
        aria-busy={sending}
        className="mt-1 inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-60"
      >
        {sending ? "Sending…" : "Send My Inquiry"}{" "}
        <ArrowRight className="size-4" aria-hidden="true" />
      </button>
    </form>
  );
}
